import { Button } from "@frontend/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@frontend/components/ui/dialog"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@frontend/components/ui/select"
import { Switch } from "@frontend/components/ui/switch"
import { Plus, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

export type ModuleTemplateType = "mask" | "score" | "vector" | "image_label" | "text"

export type ModuleTemplateEditorSubmitPayload = {
  name: string
  type: ModuleTemplateType
  schema: string
  version: number
}

export type ModuleTemplateEditorInitialValue = {
  name: string
  type: string
  schema: string
  version: number
}

interface ModuleTemplateEditorDialogProps {
  open: boolean
  mode: "create" | "edit"
  initialValue?: ModuleTemplateEditorInitialValue
  isPending: boolean
  errorMessage?: string
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: ModuleTemplateEditorSubmitPayload) => void
}

type MaskDraftGroup = {
  key: string
  name: string
  labels: Array<{
    key: string
    label: string
    color: string
    instance_policy: {
      mode: "single" | "multiple"
      min_instances?: number
      max_instances?: number | null
    }
  }>
}

type MaskSeriesFilterDraft = {
  image_plane: ("transversal" | "sagittal" | "coronal")[]
  series_description: string[]
  number_of_frames: { op: "gte" | "eq" | "lte"; value: number } | null
}

type MaskDraft = {
  type: "mask"
  groups: MaskDraftGroup[]
  series_filter: MaskSeriesFilterDraft
  disallow_overlap?: boolean
}

type ScoreDraft = {
  type: "score"
  fields: Array<{
    key: string
    label: string
    type: "integer" | "categorical" | "likert"
    required: boolean
    options?: Array<{ value: string | number; label: string }>
    min?: number
    max?: number
    linked_to_instance: boolean
  }>
}

type VectorDraftGroup = {
  key: string
  name: string
  labels: Array<{ key: string; label: string; color: string }>
}

type VectorDraft = {
  type: "vector"
  groups: VectorDraftGroup[]
}

type ImageLabelDraft = {
  type: "image_label"
  options: Array<{
    value: string
    label: string
  }>
  multi_select: boolean
}

type TextDraft = {
  type: "text"
  required?: boolean
  placeholder?: string
  max_length?: number
  scope?: "case" | "series"
}

type TemplateSchemaDraft = MaskDraft | ScoreDraft | VectorDraft | ImageLabelDraft | TextDraft

const TEMPLATE_TYPES: ModuleTemplateType[] = ["mask", "score", "vector", "image_label", "text"]

function isModuleTemplateType(value: string): value is ModuleTemplateType {
  return TEMPLATE_TYPES.includes(value as ModuleTemplateType)
}

function defaultSchemaForType(type: ModuleTemplateType): TemplateSchemaDraft {
  if (type === "mask") {
    return {
      type,
      groups: [
        {
          key: "default",
          name: "Default",
          labels: [
            {
              key: "label_1",
              label: "Label 1",
              color: "#ff0000",
              instance_policy: { mode: "single" }
            }
          ]
        }
      ],
      series_filter: { image_plane: [], series_description: [], number_of_frames: null },
      disallow_overlap: false
    }
  }

  if (type === "score") {
    return {
      type,
      fields: [
        {
          key: "field_1",
          label: "Field 1",
          type: "integer",
          required: true,
          min: 0,
          max: 5,
          linked_to_instance: false
        }
      ]
    }
  }

  if (type === "vector") {
    return {
      type,
      groups: [
        {
          key: "default",
          name: "Default",
          labels: [{ key: "roi", label: "ROI", color: "#00bcd4" }]
        }
      ]
    }
  }

  if (type === "image_label") {
    return {
      type,
      options: [{ value: "option_1", label: "Option 1" }],
      multi_select: false
    }
  }

  return {
    type: "text",
    required: false,
    placeholder: "",
    scope: "case"
  }
}

function parseSchemaDraft(type: ModuleTemplateType, rawSchema: string): TemplateSchemaDraft {
  let parsed: unknown
  try {
    parsed = JSON.parse(rawSchema)
  } catch {
    return defaultSchemaForType(type)
  }

  if (!parsed || typeof parsed !== "object") {
    return defaultSchemaForType(type)
  }

  const obj = parsed as Record<string, unknown>

  if (type === "mask") {
    const parseSeriesFilter = (raw: unknown): MaskSeriesFilterDraft => {
      const empty: MaskSeriesFilterDraft = { image_plane: [], series_description: [], number_of_frames: null }
      if (!raw || typeof raw !== "object") return empty
      const f = raw as Record<string, unknown>
      const planes = Array.isArray(f.image_plane)
        ? (f.image_plane.filter(
            (p): p is "transversal" | "sagittal" | "coronal" =>
              p === "transversal" || p === "sagittal" || p === "coronal"
          ) as ("transversal" | "sagittal" | "coronal")[])
        : []
      const desc = Array.isArray(f.series_description)
        ? f.series_description.filter((s): s is string => typeof s === "string" && s.length > 0)
        : []
      let nof: MaskSeriesFilterDraft["number_of_frames"] = null
      if (f.number_of_frames && typeof f.number_of_frames === "object") {
        const nofObj = f.number_of_frames as Record<string, unknown>
        const op = nofObj.op
        const val = Number(nofObj.value)
        if ((op === "gte" || op === "eq" || op === "lte") && Number.isFinite(val)) {
          nof = { op, value: val }
        }
      }
      return { image_plane: planes, series_description: desc, number_of_frames: nof }
    }

    const parseMaskLabels = (arr: unknown[]): MaskDraftGroup["labels"] =>
      arr
        .map((item) => {
          if (!item || typeof item !== "object") return null
          const label = item as Record<string, unknown>
          const mode: "single" | "multiple" =
            label.instance_policy &&
            typeof label.instance_policy === "object" &&
            "mode" in label.instance_policy &&
            (label.instance_policy.mode === "single" || label.instance_policy.mode === "multiple")
              ? (label.instance_policy.mode as "single" | "multiple")
              : "single"
          return {
            key: String(label.key ?? ""),
            label: String(label.label ?? ""),
            color: String(label.color ?? "#ff0000"),
            instance_policy: { mode }
          }
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item))

    // Parse grouped format
    if (Array.isArray(obj.groups) && obj.groups.length > 0) {
      const groups: MaskDraftGroup[] = obj.groups
        .map((g) => {
          if (!g || typeof g !== "object") return null
          const group = g as Record<string, unknown>
          return {
            key: String(group.key ?? ""),
            name: String(group.name ?? ""),
            labels: Array.isArray(group.labels) ? parseMaskLabels(group.labels) : []
          }
        })
        .filter((g): g is NonNullable<typeof g> => Boolean(g))

      return {
        type,
        groups: groups.length > 0 ? groups : [{ key: "default", name: "Default", labels: [] }],
        series_filter: parseSeriesFilter(obj.series_filter),
        disallow_overlap: obj.disallow_overlap === true
      }
    }

    // Legacy flat labels → wrap in single default group
    const labels = Array.isArray(obj.labels) ? parseMaskLabels(obj.labels) : []
    return {
      type,
      groups: [
        {
          key: "default",
          name: "Default",
          labels:
            labels.length > 0
              ? labels
              : [
                  {
                    key: "label_1",
                    label: "Label 1",
                    color: "#ff0000",
                    instance_policy: { mode: "single" }
                  }
                ]
        }
      ],
      series_filter: parseSeriesFilter(obj.series_filter),
      disallow_overlap: obj.disallow_overlap === true
    }
  }

  if (type === "score") {
    const fields = Array.isArray(obj.fields)
      ? obj.fields
          .map((item) => {
            if (!item || typeof item !== "object") return null
            const field = item as Record<string, unknown>
            const fieldType: "integer" | "categorical" | "likert" =
              field.type === "integer" || field.type === "categorical" || field.type === "likert"
                ? field.type
                : "integer"
            const options = Array.isArray(field.options)
              ? field.options
                  .map((opt) => {
                    if (!opt || typeof opt !== "object") return null
                    const option = opt as Record<string, unknown>
                    return {
                      value: typeof option.value === "number" ? option.value : String(option.value ?? ""),
                      label: String(option.label ?? "")
                    }
                  })
                  .filter((opt): opt is NonNullable<typeof opt> => Boolean(opt))
              : undefined

            return {
              key: String(field.key ?? ""),
              label: String(field.label ?? ""),
              type: fieldType,
              required: field.required !== false,
              options,
              min: typeof field.min === "number" ? field.min : undefined,
              max: typeof field.max === "number" ? field.max : undefined,
              linked_to_instance: Boolean(field.linked_to_instance)
            }
          })
          .filter((item): item is NonNullable<typeof item> => Boolean(item))
      : []

    return {
      type,
      fields:
        fields.length > 0
          ? fields
          : [
              {
                key: "field_1",
                label: "Field 1",
                type: "integer",
                required: true,
                min: 0,
                max: 5,
                linked_to_instance: false
              }
            ]
    }
  }

  if (type === "vector") {
    const parseLabels = (arr: unknown[]): Array<{ key: string; label: string; color: string }> =>
      arr
        .map((item) => {
          if (!item || typeof item !== "object") return null
          const label = item as Record<string, unknown>
          return {
            key: String(label.key ?? ""),
            label: String(label.label ?? ""),
            color: String(label.color ?? "#00bcd4")
          }
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item))

    if (Array.isArray(obj.groups) && obj.groups.length > 0) {
      const groups: VectorDraftGroup[] = obj.groups
        .map((g) => {
          if (!g || typeof g !== "object") return null
          const group = g as Record<string, unknown>
          return {
            key: String(group.key ?? ""),
            name: String(group.name ?? ""),
            labels: Array.isArray(group.labels) ? parseLabels(group.labels) : []
          }
        })
        .filter((g): g is NonNullable<typeof g> => Boolean(g))

      return {
        type,
        groups: groups.length > 0 ? groups : [{ key: "default", name: "Default", labels: [] }]
      }
    }

    // Legacy flat labels → wrap in single default group
    const labels = Array.isArray(obj.labels) ? parseLabels(obj.labels) : []
    return {
      type,
      groups: [{ key: "default", name: "Default", labels }]
    }
  }

  if (type === "image_label") {
    const options = Array.isArray(obj.options)
      ? obj.options
          .map((item) => {
            if (!item || typeof item !== "object") return null
            const option = item as Record<string, unknown>
            return {
              value: String(option.value ?? ""),
              label: String(option.label ?? "")
            }
          })
          .filter((item): item is NonNullable<typeof item> => Boolean(item))
      : []

    return {
      type,
      options: options.length > 0 ? options : [{ value: "option_1", label: "Option 1" }],
      multi_select: Boolean(obj.multi_select)
    }
  }

  return {
    type,
    required: obj.required === true,
    placeholder: typeof obj.placeholder === "string" ? obj.placeholder : "",
    max_length: typeof obj.max_length === "number" ? obj.max_length : undefined,
    scope: obj.scope === "series" ? "series" : "case"
  }
}

export function ModuleTemplateEditorDialog({
  open,
  mode,
  initialValue,
  isPending,
  errorMessage,
  onOpenChange,
  onSubmit
}: ModuleTemplateEditorDialogProps) {
  const initialType = useMemo<ModuleTemplateType>(() => {
    if (initialValue && isModuleTemplateType(initialValue.type)) {
      return initialValue.type
    }
    return "mask"
  }, [initialValue])

  const [name, setName] = useState("")
  const [type, setType] = useState<ModuleTemplateType>(initialType)
  const [version, setVersion] = useState<number>(1)
  const [schemaDraft, setSchemaDraft] = useState<TemplateSchemaDraft>(defaultSchemaForType(initialType))
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    const nextType = initialValue && isModuleTemplateType(initialValue.type) ? initialValue.type : "mask"

    setName(initialValue?.name ?? "")
    setType(nextType)
    setVersion(initialValue?.version ?? 1)
    setSchemaDraft(
      initialValue?.schema ? parseSchemaDraft(nextType, initialValue.schema) : defaultSchemaForType(nextType)
    )
    setLocalError(null)
  }, [initialValue, open])

  const title = mode === "create" ? "Create Template" : "Edit Template"
  const description =
    mode === "create"
      ? "Create a new template and automatically enable it on this project."
      : "Editing creates a new template version and remaps this project to it."

  const validateDraft = (): string | null => {
    if (!name.trim()) return "Template name is required."
    if (!Number.isInteger(version) || version < 1) return "Version must be at least 1."

    if (schemaDraft.type === "mask") {
      if (schemaDraft.groups.length === 0) return "Mask templates require at least one group."
      if (schemaDraft.groups.some((g) => !g.name.trim())) return "Each mask group requires a name."
      const allLabels = schemaDraft.groups.flatMap((g) => g.labels)
      if (allLabels.length === 0) return "Mask templates require at least one label."
      if (allLabels.some((label) => !(label.key.trim() && label.label.trim()))) {
        return "Each mask label requires key and label."
      }
      const labelKeys = allLabels.map((l) => l.key)
      if (new Set(labelKeys).size !== labelKeys.length) {
        return "Label keys must be unique across all groups."
      }
    }

    if (schemaDraft.type === "score") {
      if (schemaDraft.fields.length === 0) return "Score templates require at least one field."
      if (schemaDraft.fields.some((field) => !(field.key.trim() && field.label.trim()))) {
        return "Each score field requires key and label."
      }
    }

    if (schemaDraft.type === "image_label") {
      if (schemaDraft.options.length === 0) {
        return "Image label templates require at least one option."
      }
      if (schemaDraft.options.some((option) => !(option.value.trim() && option.label.trim()))) {
        return "Each image label option requires value and label."
      }
    }

    if (schemaDraft.type === "vector") {
      if (schemaDraft.groups.length === 0) return "At least one group is required."
      if (schemaDraft.groups.some((g) => !g.name.trim())) return "Each group requires a name."
      const allLabels = schemaDraft.groups.flatMap((g) => g.labels)
      if (allLabels.some((label) => !(label.key.trim() && label.label.trim()))) {
        return "Each vector label requires key and label."
      }
      const keys = allLabels.map((l) => l.key.trim())
      if (new Set(keys).size !== keys.length) {
        return "Label keys must be unique across all groups."
      }
    }

    return null
  }

  const handleSubmit = () => {
    const validationError = validateDraft()
    if (validationError) {
      setLocalError(validationError)
      return
    }

    setLocalError(null)

    // Clean series_filter: strip empty/null fields and omit entirely if nothing set
    let cleanedDraft = schemaDraft
    if (schemaDraft.type === "mask") {
      const { series_filter, ...rest } = schemaDraft
      const sf = series_filter
      const hasPlanes = sf.image_plane.length > 0
      const hasDesc = sf.series_description.length > 0
      const hasFrames = sf.number_of_frames != null
      if (hasPlanes || hasDesc || hasFrames) {
        const cleaned: Record<string, unknown> = {}
        if (hasPlanes) cleaned.image_plane = sf.image_plane
        if (hasDesc) cleaned.series_description = sf.series_description
        if (hasFrames) cleaned.number_of_frames = sf.number_of_frames
        cleanedDraft = { ...rest, series_filter: cleaned as MaskSeriesFilterDraft }
      } else {
        cleanedDraft = rest as MaskDraft
      }
    }

    onSubmit({
      name: name.trim(),
      type,
      schema: JSON.stringify(cleanedDraft),
      version
    })
  }

  const setScoreFieldType = (idx: number, nextType: "integer" | "categorical" | "likert") => {
    if (schemaDraft.type !== "score") return
    const nextFields = schemaDraft.fields.map((field, fieldIdx) => {
      if (idx !== fieldIdx) return field
      if (nextType === "integer") {
        return {
          ...field,
          type: nextType,
          options: undefined,
          min: field.min ?? 0,
          max: field.max ?? 5
        }
      }
      return {
        ...field,
        type: nextType,
        min: undefined,
        max: undefined,
        options: field.options && field.options.length > 0 ? field.options : [{ value: "option_1", label: "Option 1" }]
      }
    })

    setSchemaDraft({ ...schemaDraft, fields: nextFields })
  }

  const longValueSelectContentClass = "w-auto min-w-[var(--anchor-width)] max-w-[90vw]"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(95vw,1100px)] sm:max-w-[1100px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="template-name">Template Name</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Lesion Segmentation"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="template-version">Version</Label>
            <Input
              id="template-version"
              type="number"
              min={1}
              value={String(version)}
              onChange={(event) => {
                const next = Number(event.target.value)
                setVersion(Number.isFinite(next) ? Math.max(1, Math.trunc(next)) : 1)
              }}
            />
          </div>
          <div className="space-y-1 md:col-span-3">
            <Label>Template Type</Label>
            <Select
              value={type}
              onValueChange={(value) => {
                if (!value) return
                if (!isModuleTemplateType(value)) return
                if (mode === "edit") return
                setType(value)
                setSchemaDraft(defaultSchemaForType(value))
              }}
              disabled={mode === "edit"}
            >
              <SelectTrigger className="w-full min-w-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={longValueSelectContentClass}>
                <SelectItem value="mask">Mask</SelectItem>
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="vector">Vector</SelectItem>
                <SelectItem value="image_label">Image Label</SelectItem>
                <SelectItem value="text">Text</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {schemaDraft.type === "mask" && (
          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Mask Label Groups</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const nextKey = `group_${schemaDraft.groups.length + 1}`
                  setSchemaDraft({
                    ...schemaDraft,
                    groups: [
                      ...schemaDraft.groups,
                      { key: nextKey, name: `Group ${schemaDraft.groups.length + 1}`, labels: [] }
                    ]
                  })
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Group
              </Button>
            </div>

            {schemaDraft.groups.map((group, groupIdx) => (
              <div key={group.key} className="rounded border p-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Group Name</Label>
                    <Input
                      value={group.name}
                      onChange={(event) => {
                        const groups = [...schemaDraft.groups]
                        groups[groupIdx] = { ...groups[groupIdx]!, name: event.target.value }
                        setSchemaDraft({ ...schemaDraft, groups })
                      }}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="mt-5"
                    disabled={schemaDraft.groups.length <= 1}
                    title="Delete group"
                    onClick={() => {
                      const groups = schemaDraft.groups.filter((_, i) => i !== groupIdx)
                      setSchemaDraft({ ...schemaDraft, groups })
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {group.labels.map((label, labelIdx) => (
                  <div
                    key={`mask-label-${groupIdx}-${labelIdx}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_120px_auto] gap-2 items-end pl-2"
                  >
                    <div className="space-y-1">
                      <Label className="text-xs">Key</Label>
                      <Input
                        value={label.key}
                        onChange={(event) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = { ...labels[labelIdx]!, key: event.target.value }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={label.label}
                        onChange={(event) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = { ...labels[labelIdx]!, label: event.target.value }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Color</Label>
                      <Input
                        type="color"
                        value={label.color}
                        onChange={(event) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = { ...labels[labelIdx]!, color: event.target.value }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2 h-9">
                      <Switch
                        checked={label.instance_policy.mode === "multiple"}
                        onCheckedChange={(checked) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = {
                            ...labels[labelIdx]!,
                            instance_policy: { mode: checked ? "multiple" : "single" }
                          }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                      <Label className="text-xs">Multiple</Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove label"
                      onClick={() => {
                        const groups = [...schemaDraft.groups]
                        groups[groupIdx] = {
                          ...groups[groupIdx]!,
                          labels: groups[groupIdx]!.labels.filter((_, i) => i !== labelIdx)
                        }
                        setSchemaDraft({ ...schemaDraft, groups })
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  className="ml-2"
                  onClick={() => {
                    const groups = [...schemaDraft.groups]
                    const labels = [
                      ...groups[groupIdx]!.labels,
                      {
                        key: `label_${groups[groupIdx]!.labels.length + 1}`,
                        label: `Label ${groups[groupIdx]!.labels.length + 1}`,
                        color: "#ff0000",
                        instance_policy: { mode: "single" as const }
                      }
                    ]
                    groups[groupIdx] = { ...groups[groupIdx]!, labels }
                    setSchemaDraft({ ...schemaDraft, groups })
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Label
                </Button>
              </div>
            ))}
          </div>
        )}

        {schemaDraft.type === "mask" && (
          <div className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <p className="text-sm font-semibold">Disallow overlapping masks</p>
                <p className="text-xs text-muted-foreground">
                  Enforce one label per voxel. Newly drawn voxels that overlap an existing label are clipped away on
                  commit (existing labels are protected). Leave off to allow masks to overlap.
                </p>
              </div>
              <Switch
                checked={schemaDraft.disallow_overlap === true}
                onCheckedChange={(checked) => {
                  setSchemaDraft({ ...schemaDraft, disallow_overlap: checked })
                }}
              />
            </div>
          </div>
        )}

        {schemaDraft.type === "mask" && (
          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-semibold">Series Filter (optional)</p>
            <p className="text-xs text-muted-foreground">
              Restrict which series can be used for mask drawing. Leave empty to allow all series.
            </p>

            {/* Image Plane */}
            <div className="space-y-1">
              <Label className="text-xs">Image Plane</Label>
              <div className="flex gap-3">
                {(["transversal", "sagittal", "coronal"] as const).map((plane) => (
                  <label key={plane} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={schemaDraft.series_filter.image_plane.includes(plane)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...schemaDraft.series_filter.image_plane, plane]
                          : schemaDraft.series_filter.image_plane.filter((p) => p !== plane)
                        setSchemaDraft({
                          ...schemaDraft,
                          series_filter: { ...schemaDraft.series_filter, image_plane: next }
                        })
                      }}
                      className="accent-primary"
                    />
                    {plane.charAt(0).toUpperCase() + plane.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Series Description */}
            <div className="space-y-1">
              <Label className="text-xs">Series Description Contains (comma-separated substrings)</Label>
              <Input
                placeholder="e.g. t2, tse"
                value={schemaDraft.series_filter.series_description.join(", ")}
                onChange={(e) => {
                  const raw = e.target.value
                  const parts =
                    raw === ""
                      ? []
                      : raw
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean)
                  setSchemaDraft({
                    ...schemaDraft,
                    series_filter: { ...schemaDraft.series_filter, series_description: parts }
                  })
                }}
              />
            </div>

            {/* Number of Frames */}
            <div className="space-y-1">
              <Label className="text-xs">Number of Frames</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={schemaDraft.series_filter.number_of_frames?.op ?? ""}
                  onValueChange={(v) => {
                    if (!v) {
                      setSchemaDraft({
                        ...schemaDraft,
                        series_filter: { ...schemaDraft.series_filter, number_of_frames: null }
                      })
                    } else {
                      const op = v as "gte" | "eq" | "lte"
                      setSchemaDraft({
                        ...schemaDraft,
                        series_filter: {
                          ...schemaDraft.series_filter,
                          number_of_frames: { op, value: schemaDraft.series_filter.number_of_frames?.value ?? 1 }
                        }
                      })
                    }
                  }}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gte">≥</SelectItem>
                    <SelectItem value="eq">=</SelectItem>
                    <SelectItem value="lte">≤</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={0}
                  className="w-24"
                  disabled={!schemaDraft.series_filter.number_of_frames}
                  value={schemaDraft.series_filter.number_of_frames?.value ?? ""}
                  onChange={(e) => {
                    const val = Number.parseInt(e.target.value, 10)
                    if (!(Number.isFinite(val) && schemaDraft.series_filter.number_of_frames)) return
                    setSchemaDraft({
                      ...schemaDraft,
                      series_filter: {
                        ...schemaDraft.series_filter,
                        number_of_frames: { ...schemaDraft.series_filter.number_of_frames, value: Math.max(0, val) }
                      }
                    })
                  }}
                />
                {schemaDraft.series_filter.number_of_frames && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-muted-foreground"
                    onClick={() =>
                      setSchemaDraft({
                        ...schemaDraft,
                        series_filter: { ...schemaDraft.series_filter, number_of_frames: null }
                      })
                    }
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {schemaDraft.type === "score" && (
          <div className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Score Fields</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setSchemaDraft({
                    ...schemaDraft,
                    fields: [
                      ...schemaDraft.fields,
                      {
                        key: `field_${schemaDraft.fields.length + 1}`,
                        label: `Field ${schemaDraft.fields.length + 1}`,
                        type: "integer",
                        required: true,
                        min: 0,
                        max: 5,
                        linked_to_instance: false
                      }
                    ]
                  })
                }
              >
                <Plus className="h-4 w-4 mr-1" /> Add Field
              </Button>
            </div>

            {schemaDraft.fields.map((field, idx) => (
              <div key={`score-field-${idx}`} className="space-y-2 rounded border p-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Key</Label>
                    <Input
                      value={field.key}
                      onChange={(event) => {
                        const next = [...schemaDraft.fields]
                        next[idx] = { ...next[idx]!, key: event.target.value }
                        setSchemaDraft({ ...schemaDraft, fields: next })
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Label</Label>
                    <Input
                      value={field.label}
                      onChange={(event) => {
                        const next = [...schemaDraft.fields]
                        next[idx] = { ...next[idx]!, label: event.target.value }
                        setSchemaDraft({ ...schemaDraft, fields: next })
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={field.type}
                      onValueChange={(value) => {
                        if (value !== "integer" && value !== "categorical" && value !== "likert") {
                          return
                        }
                        setScoreFieldType(idx, value)
                      }}
                    >
                      <SelectTrigger className="w-full min-w-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={longValueSelectContentClass}>
                        <SelectItem value="integer">Integer</SelectItem>
                        <SelectItem value="categorical">Categorical</SelectItem>
                        <SelectItem value="likert">Likert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4 h-9 pt-5">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={field.required}
                        onCheckedChange={(checked) => {
                          const next = [...schemaDraft.fields]
                          next[idx] = { ...next[idx]!, required: Boolean(checked) }
                          setSchemaDraft({ ...schemaDraft, fields: next })
                        }}
                      />
                      <Label className="text-xs">Required</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={field.linked_to_instance}
                        onCheckedChange={(checked) => {
                          const next = [...schemaDraft.fields]
                          next[idx] = {
                            ...next[idx]!,
                            linked_to_instance: Boolean(checked)
                          }
                          setSchemaDraft({ ...schemaDraft, fields: next })
                        }}
                      />
                      <Label className="text-xs">Linked</Label>
                    </div>
                  </div>
                </div>

                {field.type === "integer" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Min</Label>
                      <Input
                        type="number"
                        value={field.min ?? ""}
                        onChange={(event) => {
                          const value = event.target.value
                          const next = [...schemaDraft.fields]
                          next[idx] = {
                            ...next[idx]!,
                            min: value === "" ? undefined : Number(value)
                          }
                          setSchemaDraft({ ...schemaDraft, fields: next })
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Max</Label>
                      <Input
                        type="number"
                        value={field.max ?? ""}
                        onChange={(event) => {
                          const value = event.target.value
                          const next = [...schemaDraft.fields]
                          next[idx] = {
                            ...next[idx]!,
                            max: value === "" ? undefined : Number(value)
                          }
                          setSchemaDraft({ ...schemaDraft, fields: next })
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Options</Label>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const next = [...schemaDraft.fields]
                          const options = next[idx]?.options ?? []
                          next[idx] = {
                            ...next[idx]!,
                            options: [
                              ...options,
                              {
                                value: `option_${options.length + 1}`,
                                label: `Option ${options.length + 1}`
                              }
                            ]
                          }
                          setSchemaDraft({ ...schemaDraft, fields: next })
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Option
                      </Button>
                    </div>
                    {(field.options ?? []).map((option, optionIdx) => (
                      <div
                        key={`score-option-${idx}-${optionIdx}`}
                        className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end"
                      >
                        <div className="space-y-1">
                          <Label className="text-xs">Value</Label>
                          <Input
                            value={String(option.value)}
                            onChange={(event) => {
                              const next = [...schemaDraft.fields]
                              const options = [...(next[idx]?.options ?? [])]
                              options[optionIdx] = {
                                ...options[optionIdx]!,
                                value: event.target.value
                              }
                              next[idx] = { ...next[idx]!, options }
                              setSchemaDraft({ ...schemaDraft, fields: next })
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Label</Label>
                          <Input
                            value={option.label}
                            onChange={(event) => {
                              const next = [...schemaDraft.fields]
                              const options = [...(next[idx]?.options ?? [])]
                              options[optionIdx] = {
                                ...options[optionIdx]!,
                                label: event.target.value
                              }
                              next[idx] = { ...next[idx]!, options }
                              setSchemaDraft({ ...schemaDraft, fields: next })
                            }}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Remove option"
                          onClick={() => {
                            const next = [...schemaDraft.fields]
                            const options = (next[idx]?.options ?? []).filter((_, localIdx) => localIdx !== optionIdx)
                            next[idx] = { ...next[idx]!, options }
                            setSchemaDraft({ ...schemaDraft, fields: next })
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={schemaDraft.fields.length <= 1}
                    onClick={() => {
                      const next = schemaDraft.fields.filter((_, fieldIdx) => fieldIdx !== idx)
                      setSchemaDraft({ ...schemaDraft, fields: next })
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1 text-destructive" /> Remove Field
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {schemaDraft.type === "vector" && (
          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Vector Label Groups</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const nextKey = `group_${schemaDraft.groups.length + 1}`
                  setSchemaDraft({
                    ...schemaDraft,
                    groups: [
                      ...schemaDraft.groups,
                      { key: nextKey, name: `Group ${schemaDraft.groups.length + 1}`, labels: [] }
                    ]
                  })
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Group
              </Button>
            </div>

            {schemaDraft.groups.map((group, groupIdx) => (
              <div key={group.key} className="rounded border p-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Group Name</Label>
                    <Input
                      value={group.name}
                      onChange={(event) => {
                        const groups = [...schemaDraft.groups]
                        groups[groupIdx] = { ...groups[groupIdx]!, name: event.target.value }
                        setSchemaDraft({ ...schemaDraft, groups })
                      }}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="mt-5"
                    disabled={schemaDraft.groups.length <= 1}
                    title="Delete group"
                    onClick={() => {
                      const groups = schemaDraft.groups.filter((_, i) => i !== groupIdx)
                      setSchemaDraft({ ...schemaDraft, groups })
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {group.labels.map((label, labelIdx) => (
                  <div
                    key={`vector-label-${groupIdx}-${labelIdx}`}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_auto] gap-2 items-end pl-2"
                  >
                    <div className="space-y-1">
                      <Label className="text-xs">Key</Label>
                      <Input
                        value={label.key}
                        onChange={(event) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = { ...labels[labelIdx]!, key: event.target.value }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={label.label}
                        onChange={(event) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = { ...labels[labelIdx]!, label: event.target.value }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Color</Label>
                      <Input
                        type="color"
                        value={label.color}
                        onChange={(event) => {
                          const groups = [...schemaDraft.groups]
                          const labels = [...groups[groupIdx]!.labels]
                          labels[labelIdx] = { ...labels[labelIdx]!, color: event.target.value }
                          groups[groupIdx] = { ...groups[groupIdx]!, labels }
                          setSchemaDraft({ ...schemaDraft, groups })
                        }}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remove label"
                      onClick={() => {
                        const groups = [...schemaDraft.groups]
                        groups[groupIdx] = {
                          ...groups[groupIdx]!,
                          labels: groups[groupIdx]!.labels.filter((_, i) => i !== labelIdx)
                        }
                        setSchemaDraft({ ...schemaDraft, groups })
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}

                <Button
                  size="sm"
                  variant="outline"
                  className="ml-2"
                  onClick={() => {
                    const groups = [...schemaDraft.groups]
                    const allLabels = groups.flatMap((g) => g.labels)
                    const nextIdx = allLabels.length + 1
                    groups[groupIdx] = {
                      ...groups[groupIdx]!,
                      labels: [
                        ...groups[groupIdx]!.labels,
                        { key: `label_${nextIdx}`, label: `Label ${nextIdx}`, color: "#00bcd4" }
                      ]
                    }
                    setSchemaDraft({ ...schemaDraft, groups })
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Label
                </Button>
              </div>
            ))}
          </div>
        )}

        {schemaDraft.type === "image_label" && (
          <div className="space-y-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Options</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setSchemaDraft({
                    ...schemaDraft,
                    options: [
                      ...schemaDraft.options,
                      {
                        value: `option_${schemaDraft.options.length + 1}`,
                        label: `Option ${schemaDraft.options.length + 1}`
                      }
                    ]
                  })
                }
              >
                <Plus className="h-4 w-4 mr-1" /> Add Option
              </Button>
            </div>

            {schemaDraft.options.map((option, idx) => (
              <div key={`image-option-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Value</Label>
                  <Input
                    value={option.value}
                    onChange={(event) => {
                      const next = [...schemaDraft.options]
                      next[idx] = { ...next[idx]!, value: event.target.value }
                      setSchemaDraft({ ...schemaDraft, options: next })
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Label</Label>
                  <Input
                    value={option.label}
                    onChange={(event) => {
                      const next = [...schemaDraft.options]
                      next[idx] = { ...next[idx]!, label: event.target.value }
                      setSchemaDraft({ ...schemaDraft, options: next })
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove option"
                  disabled={schemaDraft.options.length <= 1}
                  onClick={() => {
                    const next = schemaDraft.options.filter((_, optionIdx) => optionIdx !== idx)
                    setSchemaDraft({ ...schemaDraft, options: next })
                  }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}

            <div className="flex items-center gap-2 pt-1">
              <Switch
                checked={schemaDraft.multi_select}
                onCheckedChange={(checked) => setSchemaDraft({ ...schemaDraft, multi_select: Boolean(checked) })}
              />
              <Label className="text-xs">Allow multiple selections</Label>
            </div>
          </div>
        )}

        {schemaDraft.type === "text" && (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-sm font-semibold">Text Module Settings</p>
            <div className="flex items-center gap-2 pt-1">
              <Switch
                checked={schemaDraft.required === true}
                onCheckedChange={(checked) => setSchemaDraft({ ...schemaDraft, required: Boolean(checked) })}
              />
              <Label className="text-xs">Required</Label>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Placeholder</Label>
              <Input
                value={schemaDraft.placeholder ?? ""}
                onChange={(event) => setSchemaDraft({ ...schemaDraft, placeholder: event.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Max Length (optional)</Label>
                <Input
                  type="number"
                  min={1}
                  value={schemaDraft.max_length ?? ""}
                  onChange={(event) => {
                    const value = event.target.value
                    setSchemaDraft({
                      ...schemaDraft,
                      max_length: value === "" ? undefined : Math.max(1, Number(value))
                    })
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Scope</Label>
                <Select
                  value={schemaDraft.scope ?? "case"}
                  onValueChange={(value) => {
                    if (value !== "case" && value !== "series") return
                    setSchemaDraft({ ...schemaDraft, scope: value })
                  }}
                >
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={longValueSelectContentClass}>
                    <SelectItem value="case">Case</SelectItem>
                    <SelectItem value="series">Series</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {(localError || errorMessage) && <p className="text-sm text-destructive">{localError ?? errorMessage}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending
              ? mode === "create"
                ? "Creating..."
                : "Saving..."
              : mode === "create"
                ? "Create Template"
                : "Save Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
